import { useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import httpInstances from '@/api/axiosInstance';
import {
  defaultExportSettings,
  ExportSettingsType
} from '@/pages/DataHub/Export/ExportSettingsContext';
import { getFromToValues } from '@/pages/DataHub/Export/utils/getFromToValues';
import { RootState } from 'store';

let timerId: any = null;
const useDataHubExport = () => {
  const { dataHubs, activeDataHubId } = useSelector((state: RootState) => state.dataHub);
  const activeDataHub = dataHubs.find(dataHub => dataHub.id === activeDataHubId);
  const [exportSettings, setExportSettings] = useState<ExportSettingsType>({
    ...defaultExportSettings,
    ...(activeDataHub?.exportSettings ?? {})
  });
  const exportSettingsMemo = useMemo(
    () => ({ exportSettings, setExportSettings }),
    [activeDataHubId, dataHubs, activeDataHub]
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [exportError, setExportError] = useState<boolean>(false);
  const [exported, setExported] = useState<boolean>(false);
  const [previewOptionId, setPreviewOptionId] = useState<string>('');

  useEffect(() => {
    setExported(false);
  }, [activeDataHub]);

  const isExcelPreview = ['excel?type=simple', 'excel?type=advanced'].includes(previewOptionId);

  useEffect(() => {
    clearTimeout(timerId);
  }, [previewOptionId]);
  const { userConfig } = useSelector((state: RootState) => state.userConfig);
  const { fromValue, toValue } = getFromToValues(exportSettings);
  const urlToParam = toValue ? `&to=${toValue}` : '';

  const getExportBody = () => ({
    dataHubId: activeDataHubId,
    symbols:
      activeDataHub?.symbols.map(symbol => ({
        group: 'Prod_Indexes',
        key: {
          code: symbol.Code,
          periodType: symbol.PeriodType,
          timeRef: symbol.TimeRef,
          period: symbol.Period
        }
      })) ?? []
  });

  const exportData = async (type: string) => {
    setLoading(true);
    try {
      const urlType = type.startsWith('excel') ? type : `third-party?type=${type}`;
      const endpoint = `export/${urlType}&from=${fromValue}${urlToParam}&metadata=${exportSettings.includeMetadata}&timezone=${userConfig.timezone.value}&dateFormat=${userConfig.dateFormat.value}`;
      await httpInstances.axiosGoInstance.post(endpoint, getExportBody());
      setExported(true);
      if (!type.startsWith('excel')) {
        timerId = setTimeout(() => {
          setExported(false);
          setPreviewOptionId('');
        }, 5000);
      }
    } catch (error) {
      console.error(error);
      setExportError(true);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    exported,
    exportError,
    setExportError,
    isExcelPreview,
    setExported,
    exportData,
    previewOptionId,
    setPreviewOptionId,
    exportSettingsMemo
  };
};

export default useDataHubExport;
